/**
 * État partagé des notifications et des alertes.
 *
 * `NotificationBadge` (dans la barre de navigation) et `NotificationPanel` sont
 * montés ensemble sur chaque page. Chacun appelait ses propres hooks, si bien
 * que `useNotifications` tournait en double et `useAlerts` aussi (une fois
 * directement pour le badge, une fois via `useAlertsWithToast` pour le panneau).
 * Résultat : six requêtes d'alertes par page au lieu de trois, et deux boucles
 * de rafraîchissement concurrentes toutes les 30 secondes.
 *
 * Les hooks sont désormais montés une seule fois, ici, et leur résultat est
 * distribué. Les composants ne changent pas de comportement : ils lisent les
 * mêmes champs, mais sur une source unique.
 */
import { createContext, useContext, type ReactNode } from 'react';

import { useNotifications } from '../hooks/useNotifications';
import { useAlertsWithToast } from '../hooks/useAlertsWithToast';

type Notifications = ReturnType<typeof useNotifications>;
type Alertes = ReturnType<typeof useAlertsWithToast>;

interface ValeurContexte {
  notifications: Notifications;
  alertes: Alertes;
}

const Contexte = createContext<ValeurContexte | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  // Un seul montage de chaque hook pour toute l'application.
  const notifications = useNotifications();
  const alertes = useAlertsWithToast();

  return (
    <Contexte.Provider value={{ notifications, alertes }}>
      {children}
    </Contexte.Provider>
  );
}

function useContexte(appelant: string): ValeurContexte {
  const valeur = useContext(Contexte);
  if (!valeur) {
    throw new Error(`${appelant} doit être rendu à l'intérieur de <NotificationsProvider>`);
  }
  return valeur;
}

/** Notifications partagées (ex-`useNotifications`). */
export function useNotificationsPartagees(): Notifications {
  return useContexte('useNotificationsPartagees').notifications;
}

/** Alertes partagées, avec les toasts (ex-`useAlertsWithToast`). */
export function useAlertesPartagees(): Alertes {
  return useContexte('useAlertesPartagees').alertes;
}
